import logger from '../utils/logger';
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { authService } from '../services/authService';

// Add axios request interceptor for debugging
axios.interceptors.request.use(
  config => {
    logger.debug('Making request to:', config.method?.toUpperCase(), config.url);
    logger.debug('Request data:', config.data);
    return config;
  },
  error => {
    logger.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add axios response interceptor for debugging
axios.interceptors.response.use(
  response => {
    logger.debug('Response from:', response.config.url, 'Status:', response.status);
    return response;
  },
  error => {
    logger.error('Response error:', error.response?.status, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from httpOnly cookies on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Try to get current user from server (using httpOnly cookie)
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          logger.debug('User authenticated from cookie:', currentUser);
          setUser(currentUser);

          // Store user info (not token) in localStorage for offline reference
          localStorage.setItem('user', JSON.stringify(currentUser));
        } else {
          // No valid session, clear any stale data
          localStorage.removeItem('user');
        }
      } catch (error) {
        logger.error('Failed to initialize auth state:', error);
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const responseData = response.data;

      // Check if MFA is required (backend returns 202 ACCEPTED with mfaRequired flag)
      if (responseData.data && responseData.data.mfaRequired) {
        return {
          success: true,
          mfaRequired: true,
          userId: responseData.data.userId,
          email: responseData.data.email,
          message: responseData.data.message || 'MFA verification required'
        };
      }

      // Extract user data from the response
      // Backend returns ApiResponse with structure: { success, message, data: { user: userDTO, ... } }
      const userInfo = responseData.data?.user || {};

      // Store user data in context and localStorage (no tokens)
      setUser(userInfo);
      localStorage.setItem('user', JSON.stringify(userInfo));

      return { success: true, data: userInfo };
    } catch (error) {
      logger.error('Login failed:', error.response?.data?.message || error.message);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed. Please try again.'
      };
    }
  }, []);

  const signup = useCallback(async (email, password, fullName) => {
    try {
      const response = await authService.signup(email, password, fullName);
      const responseData = response.data;

      // Extract user data from the response
      // Backend returns ApiResponse with structure: { success, message, data: { user: userDTO, ... } }
      const userInfo = responseData.data?.user || {};

      logger.debug('Signup successful, user info:', userInfo);

      // Store user data in context and localStorage (no tokens)
      setUser(userInfo);
      localStorage.setItem('user', JSON.stringify(userInfo));

      return { success: true, data: userInfo };
    } catch (error) {
      logger.error('Signup failed:', error.response?.data?.message || error.message);
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  }, []);

  const logout = useCallback(async () => {
    // Clear user from context and storage immediately so route guards react
    // before the network round-trip completes (prevents logout flicker where
    // AuthPage briefly sees a still-authenticated user and bounces to /home).
    setUser(null);
    localStorage.removeItem('user');

    // Use the authService to logout (clears httpOnly cookies)
    try {
      await authService.logout();
    } catch (error) {
      logger.error('Logout request failed:', error);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        logger.debug('User refreshed:', currentUser);
        setUser(currentUser);
        localStorage.setItem('user', JSON.stringify(currentUser));
      }
    } catch (error) {
      logger.error('Failed to refresh user:', error);
    }
  }, []);

  const isAuthenticated = () => {
    return !!user;
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      login,
      signup,
      logout,
      refreshUser,
      isAuthenticated
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
